/**
 * Purchase response transformation utilities for Viaja Ahorrando.
 * Handles passenger data normalization when Viaja Ahorrando is active.
 */

import store from '@/store';
import { isViajaAhorrandoActiveInPurchase } from 'selectors/purchaseSelectors';

/**
 * Parse full name into first_name and last_name.
 * For format "ROJAS FLORES VICTOR AZAEL", assumes:
 * - First 1-2 words = last_name (surname)
 * - Last 1-2 words = first_name (given name)
 * This is common in Latin American formats where surname comes before given name.
 * @param {string} fullName - Full name string
 * @returns {Object} Object with first_name and last_name properties
 */
const parseFullName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return { first_name: '', last_name: '' };
  }

  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { first_name: '', last_name: '' };
  }

  if (parts.length === 1) {
    return { first_name: parts[0], last_name: '' };
  }

  if (parts.length === 2) {
    // Format: "ROJAS VICTOR" -> { last_name: "ROJAS", first_name: "VICTOR" }
    return { first_name: parts[1], last_name: parts[0] };
  }

  if (parts.length === 3) {
    // Format: "ROJAS FLORES VICTOR" -> { last_name: "ROJAS", first_name: "FLORES VICTOR" }
    return {
      first_name: parts.slice(1).join(' '),
      last_name: parts[0],
    };
  }

  // Four or more parts: split in half, first half is last_name, second half is first_name
  // Format: "ROJAS FLORES VICTOR AZAEL" -> { last_name: "ROJAS FLORES", first_name: "VICTOR AZAEL" }
  const midPoint = Math.ceil(parts.length / 2);
  return {
    first_name: parts.slice(midPoint).join(' '),
    last_name: parts.slice(0, midPoint).join(' '),
  };
};

/**
 * Check if first_name and last_name seem inverted based on word count patterns.
 * Common pattern: last_name typically has 1-2 words, first_name has 1-2 words.
 * If first_name has significantly more words than expected, they might be inverted.
 * @param {string} firstName - First name value
 * @param {string} lastName - Last name value
 * @returns {boolean} True if names appear inverted
 */
const seemInverted = (firstName, lastName) => {
  const firstWords = firstName.trim().split(/\s+/).length;
  const lastWords = lastName.trim().split(/\s+/).length;

  // Inverted if first_name has more than 2 words and last_name has 2 or fewer
  return firstWords > 2 && lastWords <= 2;
};

/**
 * Normalize a Viva passenger's data.
 * Handles name parsing and field normalization:
 * - Parses full name when first_name/last_name are missing or inverted
 * - Ensures all required fields have at least empty strings
 * - Converts null/undefined values to empty strings for form compatibility
 *
 * @param {Object} passenger - Passenger data object (in snake_case format from API)
 * @returns {Object} Normalized passenger data with guaranteed string fields
 */
export const normalizeVivaPassenger = (passenger) => {
  if (!passenger) {
    return passenger;
  }

  let firstName = (passenger.first_name || '').trim();
  let lastName = (passenger.last_name || '').trim();
  const fullName = (passenger.name || '').trim();

  if (fullName) {
    if (!firstName && !lastName) {
      // Both names missing: parse from full name
      const parsed = parseFullName(fullName);
      firstName = parsed.first_name;
      lastName = parsed.last_name;
    } else if (!firstName || !lastName) {
      // One name missing: parse and fill the missing field
      const parsed = parseFullName(fullName);
      firstName = firstName || parsed.first_name;
      lastName = lastName || parsed.last_name;
    } else if (seemInverted(firstName, lastName)) {
      // Both exist but appear inverted: attempt to correct
      const parsed = parseFullName(fullName);
      if (parsed.first_name && parsed.last_name) {
        firstName = parsed.first_name;
        lastName = parsed.last_name;
      }
    }
  }

  // Final fallback: use full name as first_name if still empty
  if (!firstName && fullName) {
    firstName = fullName;
  }

  return {
    ...passenger,
    first_name: firstName || '',
    last_name: lastName || '',
    email: passenger.email || '',
    phone: passenger.phone || '',
    phone_country: passenger.phone_country || '',
  };
};

/**
 * Normalize an array of Viva passengers.
 * Applies normalization to all passengers only when Viaja Ahorrando is active.
 * Checks internally if vivaPnr is present in purchase meta before processing.
 *
 * @param {Array} passengers - Array of passenger data objects (snake_case format from API)
 * @returns {Array} Array of normalized passenger data, or original array if not active
 */
export const normalizeVivaPassengers = (passengers) => {
  if (!passengers || !Array.isArray(passengers)) {
    return passengers;
  }

  const state = store.getState();
  const isViajaAhorrandoActive = isViajaAhorrandoActiveInPurchase(state);

  if (!isViajaAhorrandoActive) {
    return passengers;
  }

  return passengers.map((passenger) => normalizeVivaPassenger(passenger));
};
